/** Mirrors functions/platformConfig.js — single source for client-side preview */

export const DEFAULT_PLATFORM = {
  storeLocation: { lat: 13.6561836, lng: 78.9397511 },
  serviceRadiusKm: 3,
  maxOrderTotal: 1000,
  deliveryFeeTiers: [
    { maxMeters: 500, fee: 20 },
    { maxMeters: 1000, fee: 30 },
    { maxMeters: 2000, fee: 40 },
    { maxMeters: 3000, fee: 50 },
  ],
};

let activeConfig = { ...DEFAULT_PLATFORM, deliveryFeeTiers: [...DEFAULT_PLATFORM.deliveryFeeTiers] };

export const setActivePlatformConfig = (data) => {
  activeConfig = normalizePlatformConfig(data);
};

export const getActivePlatformConfig = () => activeConfig;

export function normalizePlatformConfig(data) {
  if (!data) {
    return {
      ...DEFAULT_PLATFORM,
      storeLocation: { ...DEFAULT_PLATFORM.storeLocation },
      deliveryFeeTiers: [...DEFAULT_PLATFORM.deliveryFeeTiers],
    };
  }
  const storeLocation = data.storeLocation
    || (Number.isFinite(data.storeLat) && Number.isFinite(data.storeLng)
      ? { lat: data.storeLat, lng: data.storeLng }
      : DEFAULT_PLATFORM.storeLocation);
  const tiers = Array.isArray(data.deliveryFeeTiers) && data.deliveryFeeTiers.length
    ? data.deliveryFeeTiers.map((t) => ({
      maxMeters: Number(t.maxMeters),
      fee: Number(t.fee),
    })).filter((t) => Number.isFinite(t.maxMeters) && Number.isFinite(t.fee))
    : DEFAULT_PLATFORM.deliveryFeeTiers;
  return {
    storeLocation: { ...storeLocation },
    serviceRadiusKm: Number.isFinite(data.serviceRadiusKm) ? data.serviceRadiusKm : DEFAULT_PLATFORM.serviceRadiusKm,
    maxOrderTotal: Number.isFinite(data.maxOrderTotal) ? data.maxOrderTotal : DEFAULT_PLATFORM.maxOrderTotal,
    deliveryFeeTiers: tiers.length ? tiers : [...DEFAULT_PLATFORM.deliveryFeeTiers],
  };
}

export const getStoreLocation = () => getActivePlatformConfig().storeLocation;

export const getMaxOrderTotal = () => getActivePlatformConfig().maxOrderTotal;

export const getServiceRadiusKm = () => getActivePlatformConfig().serviceRadiusKm;

export const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const feeForDistanceKm = (km, tiers) => {
  const meters = km * 1000;
  for (const tier of tiers) {
    if (meters <= tier.maxMeters) return tier.fee;
  }
  return null;
};

export const isInServiceAreaForConfig = (lat, lng, config = getActivePlatformConfig()) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  const { storeLocation, serviceRadiusKm } = config;
  return haversineKm(lat, lng, storeLocation.lat, storeLocation.lng) <= serviceRadiusKm;
};

export const calcDeliveryFeeForCoords = (lat, lng, config = getActivePlatformConfig()) => {
  if (!isInServiceAreaForConfig(lat, lng, config)) return null;
  const { storeLocation, deliveryFeeTiers } = config;
  return feeForDistanceKm(haversineKm(lat, lng, storeLocation.lat, storeLocation.lng), deliveryFeeTiers);
};

export const quoteDelivery = (lat, lng, config = getActivePlatformConfig()) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { ok: false, reason: 'missing_location' };
  }
  if (!isInServiceAreaForConfig(lat, lng, config)) {
    return { ok: false, reason: 'out_of_range' };
  }
  const { storeLocation, deliveryFeeTiers } = config;
  const distanceKm = haversineKm(lat, lng, storeLocation.lat, storeLocation.lng);
  const deliveryFee = feeForDistanceKm(distanceKm, deliveryFeeTiers);
  if (deliveryFee == null) return { ok: false, reason: 'out_of_range' };
  return { ok: true, distanceKm, deliveryFee };
};
